'use client'

import { useCallback, useEffect, useRef } from 'react'

import { useEventCallback } from './use-event-callback'

interface UsePollingProps<T> {
  pollingFn: () => Promise<{ status: 'pending' | 'success' | 'failed'; extras: Nullable<T> }>
  onSuccess?: (data: Nullable<T>) => void
  onError?: (error: Error) => void
  maxAttemptError?: () => void
  enabled: boolean
  interval?: number
  maxAttempts?: number
}

export type IdlePollingState = {
  status: 'idle'
}

export type PollingInProgressState<T> = {
  status: 'in-progress'
  data: T
}

export type PollingState<T> = IdlePollingState | PollingInProgressState<T>

export const isPollingInProgressState = <T>(state: PollingState<T>): state is PollingInProgressState<T> => {
  return state.status === 'in-progress'
}

export const usePolling = <T>({
  enabled = false,
  interval = 5000,
  maxAttempts = 10,
  maxAttemptError,
  onSuccess,
  onError,
  pollingFn,
}: UsePollingProps<T>) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null)
  const attemptCountRef = useRef<number>(0)
  const pollingFnRef = useEventCallback(pollingFn)
  const onSuccessCallbackRef = useEventCallback(onSuccess ?? function () {})
  const onErrorCallbackRef = useEventCallback(onError ?? function () {})
  const maxAttemptsErrorCallbackRef = useEventCallback(maxAttemptError ?? function () {})

  const poll = useCallback(async () => {
    try {
      if (maxAttempts && attemptCountRef.current >= maxAttempts) {
        maxAttemptsErrorCallbackRef?.()
        stopPolling()
        return
      }

      attemptCountRef.current += 1
      const result = await pollingFnRef()

      if (result.status === 'success') {
        onSuccessCallbackRef?.(result.extras)
        stopPolling()
      } else {
        if (enabled) {
          timeoutRef.current = setTimeout(poll, interval)
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        onErrorCallbackRef?.(error)
      } else {
        onErrorCallbackRef?.(new Error(String(error)))
      }
      if (enabled) {
        timeoutRef.current = setTimeout(poll, interval)
      }
    }
  }, [
    maxAttempts,
    pollingFnRef,
    maxAttemptsErrorCallbackRef,
    onErrorCallbackRef,
    enabled,
    interval,
    onSuccessCallbackRef,
  ])

  useEffect(() => {
    if (enabled) {
      poll()
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [enabled, interval, poll])

  const startPolling = () => {
    attemptCountRef.current = 0
    if (!enabled) {
      poll()
    }
  }

  const stopPolling = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      attemptCountRef.current = 0
    }
  }

  return {
    startPolling,
    stopPolling,
  }
}
